/**
 * Rewards a peon can offer and spend gold on. See docs/economy.md section 2.
 *
 * The catalog itself lives in the `rewards` table (server/db.ts); this file
 * only holds the shape and pure helpers, dependency-free like
 * shared/bounty.ts, so it runs on the server, in the browser and in the Node
 * test runner.
 */
export interface Reward {
  id: string;
  title: string;
  blurb: string;
  cost: number;
  /** Who offered it; NULL means the house. */
  created_by: string | null;
  created_at: string;
  /** 0 | 1 (SQLite boolean). Retired rewards are never offered again. */
  active: number;
  /** 0 | 1. Redeeming invites the buyer to drive the household's Tesla for a day (docs/tesla.md). */
  tesla_access: number;
}

const MAX_GIFT_LINK = 2000;

/** A readable reason `link` is not an http(s) URL of sane length (a gift card link), or null when it is fine. */
export function giftLinkError(link: string): string | null {
  const s = link.trim();
  if (s.length === 0) return "Paste a link";
  if (s.length > MAX_GIFT_LINK) return "That link is too long";
  let protocol: string;
  try {
    protocol = new URL(s).protocol;
  } catch {
    return "That is not a valid link";
  }
  if (protocol !== "https:" && protocol !== "http:") return "The link must start with https://";
  return null;
}

export function canAfford(balance: number, reward: Pick<Reward, "cost">): boolean {
  return balance >= reward.cost;
}

/**
 * Validates a new or edited reward's user-supplied fields. Returns a message
 * for the first problem found, or null when everything is in range.
 */
export function rewardInputErrors(input: { title: string; blurb: string; cost: number }): string | null {
  const title = input.title.trim();
  if (title.length < 1 || title.length > 60) return "Title must be 1 to 60 characters";
  const blurb = input.blurb.trim();
  if (blurb.length > 200) return "Blurb must be at most 200 characters";
  if (!Number.isInteger(input.cost) || input.cost < 1 || input.cost > 100000) return "Cost must be a whole number from 1 to 100000";
  return null;
}

/**
 * Whether a reward's card adds "Includes a day with the Tesla" under its description: only for a
 * reward that grants the Tesla and whose description does not already say so ("Borrow the Tesla
 * for a day." needs no second line). Both clients ask this, so the card reads the same.
 */
export function teslaLineShown(reward: Pick<Reward, "tesla_access" | "blurb">): boolean {
  return reward.tesla_access === 1 && !/\btesla\b/i.test(reward.blurb);
}
